'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { soundEnabledAtom } from '@/_state/atoms';

/**
 * Returns a playNotification() function that plays a completion chime
 * when sound is enabled and the browser tab is hidden (user switched away).
 *
 * Audio is lazily initialized and preloaded once enabled.
 * Cleaned up on unmount to avoid memory leaks.
 */
export function useNotificationSound() {
	const soundEnabled = useAtomValue(soundEnabledAtom);
	const audioRef = useRef<HTMLAudioElement | null>(null);

	const getAudio = useCallback(() => {
		if (typeof window === 'undefined') return null;
		if (!audioRef.current) {
			audioRef.current = new Audio('/sounds/notification.mp3');
			audioRef.current.preload = 'auto';
			audioRef.current.onerror = () => {
				console.debug('[sound] Failed to load notification audio');
				audioRef.current = null; // allow retry on next getAudio() call
			};
		}
		return audioRef.current;
	}, []);

	// Preload once enabled so the first play has no latency
	useEffect(() => {
		if (soundEnabled) {
			getAudio()?.load();
		}
	}, [soundEnabled, getAudio]);

	// Cleanup Audio on unmount
	useEffect(() => {
		return () => {
			if (audioRef.current) {
				audioRef.current.pause();
				audioRef.current.src = '';
				audioRef.current = null;
			}
		};
	}, []);

	const playNotification = useCallback(() => {
		if (!soundEnabled) return;
		if (typeof document === 'undefined' || !document.hidden) return;

		const audio = getAudio();
		if (!audio) return;
		audio.currentTime = 0;
		audio.play().catch(() => {
			// Autoplay policy blocked — safe to swallow
		});
	}, [soundEnabled, getAudio]);

	return { playNotification };
}
